import { useState, useEffect } from 'react';
import { Box, Typography, Link } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import {
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  buildProjectName,
  findAllureReportUrl,
  parseProjectFromUrl,
  parseReportIdFromUrl,
} from '../services/allure';
import type { AllureInputs } from '../services/allure';

// Signal colors for Allure report statistics
const SIGNAL_COLORS = {
  reliable: '#4caf50', // Green (≥90%)
  partial: '#ff9800', // Orange (75-89%)
  unreliable: '#f44336', // Red (<75%)
  noSignal: '#9e9e9e', // Gray (no data)
} as const;

/**
 * Get signal color based on percentage
 */
const getSignalColor = (percentage: number): string => {
  if (percentage >= 90) return SIGNAL_COLORS.reliable;
  if (percentage >= 75) return SIGNAL_COLORS.partial;
  return SIGNAL_COLORS.unreliable;
};

interface AllureReportLinkProps {
  runId: number;
  status: string;
  workflowInputs: AllureInputs;
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  cachedMetadata?: {
    backstage_user: string;
    triggered_at: string;
    inputs: any;
    report_url?: string | null;
    // Test statistics from artifact metadata
    passed?: number;
    failed?: number;
    broken?: number;
    skipped?: number;
    unknown?: number;
  } | null;
  cachedStats?: {
    statistic: {
      passed: number;
      total: number;
    };
    percentage: number;
    loading: boolean;
  };
}

export const AllureReportLink = ({
  runId,
  status,
  workflowInputs,
  discoveryApi,
  fetchApi,
  cachedMetadata,
  cachedStats,
}: AllureReportLinkProps) => {
  const navigate = useNavigate();
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState(false);

  const project = buildProjectName(workflowInputs);

  // Fetch the real report URL from workflow logs
  useEffect(() => {
    if (status === 'completed' && !loadingUrl) {
      // First check if we have report_url from cached_metadata
      const cachedReportUrl = cachedMetadata?.report_url;
      
      if (cachedReportUrl) {
        setReportUrl(cachedReportUrl);
        return;
      }
      
      // If not in cache, fetch from backend
      if (!reportUrl) {
        setLoadingUrl(true);
        findAllureReportUrl(discoveryApi, fetchApi, workflowInputs, runId.toString())
          .then(url => {
            if (url) {
              setReportUrl(url);
            }
          })
          .finally(() => setLoadingUrl(false));
      }
    }
  }, [status, runId, reportUrl, loadingUrl, discoveryApi, fetchApi, workflowInputs, cachedMetadata]);

  // Parse project and report_id from URL
  const displayProject = reportUrl ? parseProjectFromUrl(reportUrl) || project : project;
  const reportId = reportUrl ? parseReportIdFromUrl(reportUrl) : 'latest';

  // Calculate test statistics from artifact metadata (preferred) or cachedStats (fallback)
  let displayStats = null;

  if (cachedMetadata?.passed !== undefined) {
    // Use artifact metadata (PRIMARY SOURCE)
    const passed = cachedMetadata.passed ?? 0;
    const failed = cachedMetadata.failed ?? 0;
    const broken = cachedMetadata.broken ?? 0;
    const skipped = cachedMetadata.skipped ?? 0;
    const unknown = cachedMetadata.unknown ?? 0;
    const total = passed + failed + broken + skipped + unknown;
    const percentage = total > 0 ? (passed / total) * 100 : 0;

    displayStats = {
      passed,
      total,
      percentage,
    };
  } else if (cachedStats && !cachedStats.loading) {
    // Fallback to Allure API stats
    displayStats = {
      passed: cachedStats.statistic.passed,
      total: cachedStats.statistic.total,
      percentage: cachedStats.percentage,
    };
  }

  return (
    <Box>
      <Link
        component="button"
        onClick={() =>
          navigate(`/qa-automated-tests/reports?project=${displayProject}&report=${reportId}`)
        }
        style={{ fontWeight: 'bold', textDecoration: 'underline' }}
      >
        {loadingUrl ? 'Loading...' : displayProject}
      </Link>
      {displayStats && (
        <Typography 
          variant="caption" 
          display="block" 
          style={{ 
            color: getSignalColor(displayStats.percentage),
            fontWeight: 'bold',
          }}
        >
          {displayStats.passed}/{displayStats.total} passed (
          {displayStats.percentage.toFixed(1)}%)
        </Typography>
      )}
    </Box>
  );
};
